/* eslint-disable sonarjs/cognitive-complexity */
import type { HeaderPromotion } from '@/components/_headers/headerData'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/_ui/Carousel'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'

import React, { useEffect, useState } from 'react'

// This component will gather all the promotions from all the header items, and display them
// in a swipeable carousel. This will be displayed in the MobileDrawer component.

export const MobileDrawerPromotions = () => {
  const {
    cms: { header },
    setApp,
  } = useGlobalStore()
  const { clientRedirect } = useClientRedirect()
  const { gtm } = useGTM()

  const [promotions, setPromotions] = useState<HeaderPromotion[] | null>(null)

  useEffect(() => {
    if (!header) return
    if (header === 'error') return
    if (header === 'loading') return

    const globalPromotions = header.items
      ? header.items.flatMap((item) => {
          if (!item || !item.headerItem) return []

          const { content } = item.headerItem

          if (!content) return []

          const promos = []

          if (
            content.rightSection &&
            content.rightSection.promotion &&
            content.rightSection.promotion.title &&
            content.rightSection.promotion.description &&
            content.rightSection.promotion.image
          ) {
            promos.push(content.rightSection.promotion)
          }
          if (
            content.leftSection &&
            content.leftSection.promotion &&
            content.leftSection.promotion.title &&
            content.leftSection.promotion.description &&
            content.leftSection.promotion.image
          ) {
            promos.push(content.leftSection.promotion)
          }

          return promos
        })
      : null

    setPromotions(globalPromotions)
  }, [header])

  if (!promotions) return null

  return (
    <Carousel
      opts={{
        align: 'start',
      }}
      className={promotions.length > 1 ? 'mx-6' : ''}
    >
      <CarouselContent>
        {promotions.map((promotion) => (
          <CarouselItem key={promotion.title}>
            <a
              className="cursor-pointer no-underline"
              onClick={() => {
                ;(async () => {
                  await new Promise((resolve) => {
                    gtm.navbarClicked({
                      action: promotion.title,
                      ads_title: promotion.button.title,
                      ads_image_name: promotion.description,
                      ads_url: promotion.button.href,
                    })

                    mixpanelService
                      .trackEvent(MixpanelEvent.NAVIGATION_BAR_CLICKED, {
                        action: promotion.title,
                        ads_title: promotion.button.title,
                        ads_image_name: promotion.description,
                        ads_url: promotion.button.href,
                      })
                      .catch(() => {})

                    gtm.navbarMenuItemClicked({
                      action: 'ads_learn_more',
                      category: 'ads',
                      ads_title: promotion.title,
                      ads_image_name: promotion.image,
                      ads_url: promotion.button.href,
                    })

                    mixpanelService
                      .trackEvent(
                        MixpanelEvent.NAVIGATION_BAR_SHOP_MENU_ITEM_CLICKED,
                        {
                          action: 'ads_learn_more',
                          category: 'ads',
                          ads_title: promotion.title,
                          ads_image_name: promotion.image,
                          ads_url: promotion.button.href,
                        },
                      )
                      .catch(() => {})

                    setTimeout(resolve, 500)
                  })
                })().catch(() => {})
                ;(async () => {
                  await clientRedirect.to({ path: promotion.button.href })
                })().catch(() => {})
                setApp({ isMobileDrawerOpen: false })
              }}
            >
              <div className="flex items-start rounded-lg bg-slate-100 p-3">
                <Image
                  className=" rounded-lg"
                  height={120}
                  width={120}
                  alt="Promotion"
                  src={promotion.image}
                />

                <div className="ml-3 flex flex-col justify-center gap-3">
                  <span className="bold text-md font-bold text-primary underline">
                    {promotion.title}
                  </span>
                  <span className="text-sm  text-primary">
                    {promotion.description}
                  </span>
                </div>
              </div>
            </a>
          </CarouselItem>
        ))}
      </CarouselContent>
      {promotions.length > 1 && (
        <div className="block lg:hidden">
          <CarouselPrevious />
          <CarouselNext />
        </div>
      )}
    </Carousel>
  )
}
